import React, { useEffect, useState } from 'react';
import { APP_VERSION } from '../version';

/**
 * PWA Intro Splash mit Fade-In-Animation:
 * 1. Logo (Flammen/Silhouette) fadet rein + leicht skalieren
 * 2. Schrift „Burning Team" fadet etwas später rein
 * 3. Danach komplettes Ausblenden
 *
 * Wird nur 1× pro Browser-Session angezeigt (sessionStorage).
 * Dauer: ~2.4s
 */
export default function PwaIntroSplash() {
  const [visible, setVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !sessionStorage.getItem('bt_intro_shown');
  });
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    if (!visible) return;
    const t1 = setTimeout(() => setFadeOut(true), 2000);
    const t2 = setTimeout(() => {
      setVisible(false);
      sessionStorage.setItem('bt_intro_shown', '1');
    }, 2600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-intro-splash"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#ffffff',
        transition: 'opacity 600ms ease-out',
        opacity: fadeOut ? 0 : 1,
        pointerEvents: fadeOut ? 'none' : 'auto',
      }}
    >
      <style>{`
        @keyframes bt-logo-in {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes bt-text-in {
          0%   { opacity: 0; transform: translate(-50%, 14px); }
          100% { opacity: 1; transform: translate(-50%, 0); }
        }
        @keyframes bt-logo-glow {
          0%, 100% { filter: drop-shadow(0 0 10px rgba(251,146,60,0.25)); }
          50%      { filter: drop-shadow(0 0 22px rgba(251,146,60,0.55)); }
        }
        .bt-intro-logo {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: min(72vw, 380px);
          height: auto;
          /* Beste Qualität beim Downscale (organische Flammen-Kanten).
             "crisp-edges" wäre falsch – würde Treppen erzeugen. */
          image-rendering: auto;
          image-rendering: high-quality;
          -ms-interpolation-mode: bicubic;
          /* Hardware-Acceleration ohne sub-pixel-blur */
          will-change: opacity, filter;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          animation:
            bt-logo-in 500ms ease-out 0ms both,
            bt-logo-glow 2000ms ease-in-out 500ms infinite;
        }
        .bt-intro-text {
          position: absolute;
          top: calc(50% + min(36vw, 210px));
          left: 50%;
          transform: translateX(-50%);
          width: min(60vw, 310px);
          height: auto;
          image-rendering: auto;
          image-rendering: high-quality;
          -ms-interpolation-mode: bicubic;
          will-change: opacity, transform;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          animation: bt-text-in 700ms ease-out 600ms both;
        }
      `}</style>

      <picture>
        <source srcSet="/bt-splash-logo.webp" type="image/webp" />
        <img
          src="/bt-splash-logo.png"
          alt="BurningTeam Logo"
          className="bt-intro-logo"
          draggable={false}
          width={1080}
          height={1080}
        />
      </picture>
      <picture>
        <source srcSet="/burningteam-text.webp" type="image/webp" />
        <img
          src="/burningteam-text.png"
          alt="Burning Team"
          className="bt-intro-text"
          draggable={false}
          width={780}
          height={309}
        />
      </picture>
      <div
        data-testid="pwa-intro-version"
        style={{
          position: 'absolute',
          bottom: '24px',
          left: 0,
          right: 0,
          textAlign: 'center',
          fontSize: '11px',
          color: '#94a3b8',
          letterSpacing: '0.04em',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        v{APP_VERSION}
      </div>
    </div>
  );
}
